import { useState, useCallback } from 'react';

/**
 * Custom hook for handling touch swipe gestures
 * @param {Object} options - Configuration options
 * @param {Function} options.onSwipeLeft - Callback when user swipes left
 * @param {Function} options.onSwipeRight - Callback when user swipes right
 * @param {number} options.minSwipeDistance - Minimum distance in pixels to trigger swipe (default: 50)
 * @returns {Object} Touch event handlers to spread on the target element
 */
const useSwipe = ({ onSwipeLeft, onSwipeRight, minSwipeDistance = 50 }) => {
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const onTouchStart = useCallback((e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    }, []);

    const onTouchMove = useCallback((e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const onTouchEnd = useCallback(() => {
        if (!touchStart || !touchEnd) return;
        
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        
        if (isLeftSwipe && onSwipeLeft) {
            onSwipeLeft();
        }
        if (isRightSwipe && onSwipeRight) {
            onSwipeRight();
        }
        
        setTouchStart(null);
        setTouchEnd(null);
    }, [touchStart, touchEnd, minSwipeDistance, onSwipeLeft, onSwipeRight]);

    return {
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    };
};

export default useSwipe;
